/**
 * Multi-step form chrome: progress track, step headings, grouped field cards, quantity steppers,
 * iOS date picker host (sheet chrome is `BottomSheetPopup`), sticky action bar.
 *
 * **Consumer map:** exports are used by `src/version1/journey/JourneyScreen.tsx` (v2/v3: `version2|3/journey/…`)
 * (compose with `useMemo` + `ThemeColors` where factories apply). Add `Used by:` on new exports.
 *
 * @see atomicRules.ts
 */
import { StyleSheet, ViewStyle, TextStyle } from 'react-native';
import { hexToRgba } from '../colorUtils';
import type { ThemeColors } from '../store';
import { appTheme } from '../appTheme';

export const FORM_STEP_PROGRESS_CHROME_HEIGHT = 56;

export const formStepProgressWrap: ViewStyle = {
  paddingVertical: appTheme.spacing.md,
  paddingHorizontal: appTheme.spacing.lg,
};

export const formStepProgressTrackOuter: ViewStyle = {
  width: '100%',
};

export function createFormStepProgressTrackRailStyle(c: ThemeColors): ViewStyle {
  return {
    height: 1,
    borderRadius: 100,
    overflow: 'hidden',
    marginBottom: appTheme.spacing.md,
    backgroundColor: hexToRgba(c.border, 0.35),
  };
}

export const formStepProgressFillBase: ViewStyle = {
  height: '100%',
  borderRadius: 100,
};

export const formStepProgressNodesRow: ViewStyle = {
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingHorizontal: 2,
};

export const formStepProgressNodeColumn: ViewStyle = {
  alignItems: 'center',
  flex: 1,
};

export function createFormStepProgressNodeDiscStyle(
  c: ThemeColors,
  opts: { active: boolean },
): ViewStyle {
  return {
    width: 45,
    height: 45,
    borderRadius: 26,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: opts.active ? c.primary : c.surface,
    borderColor: opts.active ? c.primary : hexToRgba(c.border, 0.55),
    shadowColor: c.shadowColor,
    ...(opts.active ? appTheme.shadows.xs : {}),
  };
}

export function createFormStepProgressNodeGlyphStyle(
  c: ThemeColors,
  opts: { active: boolean },
): TextStyle {
  return {
    fontSize: 16,
    fontWeight: '800',
    letterSpacing: 0.2,
    color: opts.active ? c.onPrimary : c.textSecondary,
  };
}

export const formStepScreenRoot: ViewStyle = {
  flex: 1,
};

export const formStepScroll: ViewStyle = {
  flex: 1,
};

/** Centers step content on wide tablets; horizontal padding lives here. Used by: `JourneyScreen` ScrollView `contentContainerStyle`. */
export const formStepScrollContent: ViewStyle = {
  flexGrow: 1,
  width: '100%',
  maxWidth: 440,
  alignSelf: 'center',
  paddingHorizontal: appTheme.spacing.lg,
};

export const formStepPageBlock: ViewStyle = {
  paddingBottom: appTheme.spacing.xxl,
  marginTop: appTheme.spacing.lg,
};

export function createFormStepPageHeadingStyle(c: ThemeColors): TextStyle {
  return {
    fontSize: 28,
    lineHeight: 34,
    fontWeight: '800',
    letterSpacing: -0.4,
    marginBottom: 10,
    color: c.text,
  };
}

export function createFormStepPageSubheadingStyle(c: ThemeColors): TextStyle {
  return {
    fontSize: 15,
    fontWeight: '400',
    marginBottom: appTheme.spacing.xxl,
    lineHeight: 24,
    letterSpacing: 0.15,
    color: c.textSecondary,
  };
}

export function createFormStepSectionEyebrowStyle(c: ThemeColors): TextStyle {
  return {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 1.6,
    marginBottom: appTheme.spacing.md,
    color: c.textSecondary,
  };
}

/** Card shell — date, gender row container, body metrics. */
export function createFormStepGroupedFieldCardStyle(c: ThemeColors): ViewStyle {
  return {
    borderRadius: appTheme.borderRadius.xl,
    borderWidth: 1,
    padding: appTheme.card.padding.lg,
    shadowColor: c.shadowColor,
    borderColor: hexToRgba(c.border, 0.65),
    backgroundColor: c.surface,
    ...appTheme.shadows.card,
  };
}

/** Space below each stacked field card except the last (about-you step). Used by: `JourneyScreen` step 2. */
export const formStepStackedFieldMarginBelow: ViewStyle = {
  marginBottom: appTheme.spacing.md,
};

/** Tighter page block for dense step (no step title block). Used by: `JourneyScreen` step 2. */
export const formStepDensePageBlock: ViewStyle = {
  paddingBottom: appTheme.spacing.xl,
  marginTop: appTheme.spacing.sm,
};

export const formStepDateValueRow: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
};

export function createFormStepDateLeadingIconWellStyle(c: ThemeColors): ViewStyle {
  return {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: hexToRgba(c.primary, 0.14),
    backgroundColor: c.primaryFade,
  };
}

export const formStepDateCopyColumn: ViewStyle = {
  flex: 1,
  minWidth: 0,
};

export function createFormStepDatePrimaryLineStyle(c: ThemeColors): TextStyle {
  return {
    fontSize: 17,
    fontWeight: '700',
    letterSpacing: 0.2,
    marginBottom: 4,
    color: c.text,
  };
}

export function createFormStepDateSecondaryLineStyle(c: ThemeColors): TextStyle {
  return {
    fontSize: 13,
    lineHeight: 20,
    color: c.textSecondary,
  };
}

export function createFormStepInlineMetricChipSurfaceStyle(c: ThemeColors): ViewStyle {
  return {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: appTheme.borderRadius.pill,
    alignItems: 'center',
    minWidth: 52,
    backgroundColor: c.primary,
    shadowColor: c.shadowColor,
    ...appTheme.shadows.sm,
  };
}

export function createFormStepInlineMetricChipValueStyle(c: ThemeColors): TextStyle {
  return {
    fontSize: 18,
    fontWeight: '800',
    color: c.onPrimary,
  };
}

export function createFormStepInlineMetricChipUnitStyle(c: ThemeColors): TextStyle {
  return {
    fontSize: 10,
    fontWeight: '700',
    marginTop: -2,
    color: c.onPrimary,
    opacity: 0.92,
  };
}

export function createFormStepIosDatePickerHostStyle(c: ThemeColors): ViewStyle {
  return {
    minHeight: 216,
    justifyContent: 'center',
    backgroundColor: c.surface,
  };
}

export const formStepIosDatePickerFrame: ViewStyle = {
  height: 216,
};

export const formStepGroupedMetricsCardPadding: ViewStyle = {
  paddingVertical: appTheme.spacing.sm,
};

export function createFormStepGroupedCardHairlineStyle(c: ThemeColors): ViewStyle {
  return {
    height: StyleSheet.hairlineWidth,
    marginVertical: appTheme.spacing.md,
    marginHorizontal: -appTheme.card.padding.lg,
    backgroundColor: hexToRgba(c.divider, 0.85),
  };
}

export const formStepQuantityStepperBlock: ViewStyle = {
  marginBottom: 0,
};

export function createFormStepQuantityStepperCaptionStyle(c: ThemeColors): TextStyle {
  return {
    fontSize: 13,
    fontWeight: '700',
    marginBottom: appTheme.spacing.xs,
    color: c.text,
  };
}

export const formStepQuantityStepperRow: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  gap: appTheme.spacing.sm,
  marginBottom: appTheme.spacing.xs,
};

export function createFormStepQuantityStepperNudgeBtnStyle(c: ThemeColors): ViewStyle {
  return {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    borderColor: hexToRgba(c.border, 0.85),
    backgroundColor: c.surface,
    shadowColor: c.shadowColor,
    ...appTheme.shadows.xs,
  };
}

/** Value well between − / + nudges; border/fill tint from `accentColor`. */
export function createFormStepQuantityStepperValueWellStyle(opts: { accentColor: string }): ViewStyle {
  return {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'center',
    paddingVertical: appTheme.spacing.sm,
    borderWidth: 1.5,
    borderRadius: appTheme.borderRadius.lg,
    gap: 4,
    borderColor: hexToRgba(opts.accentColor, 0.38),
    backgroundColor: hexToRgba(opts.accentColor, 0.08),
  };
}

export function createFormStepQuantityStepperValueStyle(opts: { accentColor: string }): TextStyle {
  return {
    fontSize: 22,
    lineHeight: 28,
    fontWeight: '800',
    color: opts.accentColor,
  };
}

export function createFormStepQuantityStepperUnitStyle(c: ThemeColors): TextStyle {
  return {
    ...appTheme.typography.bodySmall,
    fontWeight: '700',
    color: c.textSecondary,
  };
}

export function createFormStepBoundedValueTrackStyle(c: ThemeColors): ViewStyle {
  return {
    borderRadius: 100,
    height: 30,
    justifyContent: 'center',
    overflow: 'hidden',
    marginTop: appTheme.spacing.sm,
    backgroundColor: hexToRgba(c.border, 0),
  };
}

export const formStepBoundedSliderRtlHost: ViewStyle = {
  width: '100%',
};

export const formStepBoundedSlider: ViewStyle = {
  width: '100%',
  height: 32,
};

export const formStepRtlRowReverse: ViewStyle = {
  flexDirection: 'row-reverse',
};

export function createFormStepStickyActionBarStyle(c: ThemeColors): ViewStyle {
  return {
    paddingTop: appTheme.spacing.lg,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: hexToRgba(c.divider, 0.6),
    backgroundColor: c.surface,
    shadowColor: c.shadowColor,
    ...appTheme.shadows.md,
  };
}

/** Inner row inside sticky CTA — label + trailing icon. Pair label text with `createSolidPrimaryCtaLabelStyle` from `buttons.ts`. Used by: `JourneyScreen`. */
export const formStepStickyActionBarCtaRow: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: appTheme.spacing.sm,
  width: '100%',
};
